import { inject, InjectionToken, type Type } from '@angular/core';
import { signalStoreFeature, withHooks, withMethods } from '@ngrx/signals';
import { Events, withEventHandlers } from '@ngrx/signals/events';
import { DialogService, type DynamicDialogRef } from 'primeng/dynamicdialog';
import { catchError, EMPTY, from, switchMap, tap } from 'rxjs';
import { dialogFrameDeletedUiEvents } from '../../../events/dialog/dialog-frame-deleted.events';
import { dialogPublishPeerUiEvents } from '../../../events/dialog/dialog-publish-peer.events';
import { dialogPublishSuccessUiEvents } from '../../../events/dialog/dialog-publish-success.events';
import {
  dialogPublishApiEvents,
  dialogPublishUiEvents,
} from '../../../events/dialog/dialog-publish.events';
import { dialogReuseUiEvents } from '../../../events/dialog/dialog-reuse.events';
import { dialogShortcutsUiEvents } from '../../../events/dialog/dialog-shortcuts.events';

export const DIALOG_PUBLISH_COMPONENT = new InjectionToken<
  () => Promise<Type<unknown>>
>('DIALOG_PUBLISH_COMPONENT');

export const DIALOG_REUSE_COMPONENT = new InjectionToken<
  () => Promise<Type<unknown>>
>('DIALOG_REUSE_COMPONENT');

export const DIALOG_PUBLISH_SUCCESS_COMPONENT = new InjectionToken<
  () => Promise<Type<unknown>>
>('DIALOG_PUBLISH_SUCCESS_COMPONENT');

export const DIALOG_PUBLISH_IN_PROGRESS_COMPONENT = new InjectionToken<
  () => Promise<Type<unknown>>
>('DIALOG_PUBLISH_IN_PROGRESS_COMPONENT');

export const DIALOG_PUBLISH_FAILURE_COMPONENT = new InjectionToken<
  () => Promise<Type<unknown>>
>('DIALOG_PUBLISH_FAILURE_COMPONENT');

export const DIALOG_SHORTCUTS_COMPONENT = new InjectionToken<
  () => Promise<Type<unknown>>
>('DIALOG_SHORTCUTS_COMPONENT');

export const DIALOG_FRAME_DELETED_COMPONENT = new InjectionToken<
  () => Promise<Type<unknown>>
>('DIALOG_FRAME_DELETED_COMPONENT');

export function withDialogEffects() {
  let dialogRef: DynamicDialogRef | null = null;

  const handleComponentLoadingError = (
    dialogName:
      | 'publish'
      | 'success'
      | 'publishInProgress'
      | 'publishFailure'
      | 'reuse'
      | 'shortcuts'
      | 'frameDeleted',
  ) =>
    catchError((error: unknown) => {
      console.error(`Failed to load ${dialogName} dialog component:`, error);
      dialogRef = null;
      return EMPTY;
    });

  return signalStoreFeature(
    withMethods(() => {
      return {
        cleanUpDialog() {
          if (!dialogRef) return;
          dialogRef.close();
          dialogRef = null;
        },
      };
    }),
    withEventHandlers((store) => {
      const events = inject(Events);
      const dialogService = inject(DialogService);

      const loadDialogPublishComponent = inject(DIALOG_PUBLISH_COMPONENT);
      const loadDialogReuseComponent = inject(DIALOG_REUSE_COMPONENT);
      const loadDialogPublishSuccessComponent = inject(
        DIALOG_PUBLISH_SUCCESS_COMPONENT,
      );
      const loadDialogPublishInProgressComponent = inject(
        DIALOG_PUBLISH_IN_PROGRESS_COMPONENT,
      );
      const loadDialogPublishFailureComponent = inject(
        DIALOG_PUBLISH_FAILURE_COMPONENT,
      );
      const loadDialogShortcutsComponent = inject(DIALOG_SHORTCUTS_COMPONENT);
      const loadDialogFrameDeletedComponent = inject(
        DIALOG_FRAME_DELETED_COMPONENT,
      );

      return {
        publishDialogOpened$: events
          .on(dialogPublishUiEvents.dialogOpened)
          .pipe(
            switchMap(({ payload }) =>
              from(loadDialogPublishComponent()).pipe(
                tap(
                  (component) =>
                    (dialogRef = dialogService.open(
                      component,
                      payload.configuration,
                    )),
                ),
                handleComponentLoadingError('publish'),
              ),
            ),
          ),
        publishSuccessDialogOpened$: events
          .on(dialogPublishSuccessUiEvents.dialogOpened)
          .pipe(
            switchMap(({ payload }) =>
              from(loadDialogPublishSuccessComponent()).pipe(
                tap(
                  (component) =>
                    (dialogRef = dialogService.open(component, payload)),
                ),
                handleComponentLoadingError('success'),
              ),
            ),
          ),
        publishInProgressDialogOpened$: events
          .on(dialogPublishPeerUiEvents.publishingInProgressDialogOpened)
          .pipe(
            switchMap(({ payload }) =>
              from(loadDialogPublishInProgressComponent()).pipe(
                tap(
                  (component) =>
                    (dialogRef = dialogService.open(component, payload)),
                ),
                handleComponentLoadingError('publishInProgress'),
              ),
            ),
          ),
        publishFailureDialogOpened$: events
          .on(dialogPublishPeerUiEvents.publishingFailureDialogOpened)
          .pipe(
            switchMap(({ payload }) =>
              from(loadDialogPublishFailureComponent()).pipe(
                tap(
                  (component) =>
                    (dialogRef = dialogService.open(component, payload)),
                ),
                handleComponentLoadingError('publishFailure'),
              ),
            ),
          ),
        reuseDialogOpened$: events.on(dialogReuseUiEvents.dialogOpened).pipe(
          switchMap(({ payload }) =>
            from(loadDialogReuseComponent()).pipe(
              tap((component) => {
                dialogRef = dialogService.open(component, payload);
              }),
              handleComponentLoadingError('reuse'),
            ),
          ),
        ),
        shortcutsDialogOpened$: events
          .on(dialogShortcutsUiEvents.dialogOpened)
          .pipe(
            switchMap(({ payload }) =>
              from(loadDialogShortcutsComponent()).pipe(
                tap((component) => {
                  dialogRef = dialogService.open(component, payload);
                }),
                handleComponentLoadingError('shortcuts'),
              ),
            ),
          ),
        frameDeletedDialogOpened$: events
          .on(dialogFrameDeletedUiEvents.dialogOpened)
          .pipe(
            switchMap(({ payload }) =>
              from(loadDialogFrameDeletedComponent()).pipe(
                tap((component) => {
                  dialogRef = dialogService.open(component, payload);
                }),
                handleComponentLoadingError('frameDeleted'),
              ),
            ),
          ),
        dialogClosed$: events
          .on(
            dialogPublishUiEvents.dialogClosed,
            dialogPublishUiEvents.errorsReviewed,
            dialogPublishUiEvents.seeConflictsClicked,
            dialogPublishUiEvents.seeElementsClicked,
            dialogPublishApiEvents.publishSuccess,
            dialogPublishSuccessUiEvents.dialogClosed,
            dialogPublishSuccessUiEvents.viewOpened,
            dialogPublishPeerUiEvents.dialogClosed,
            dialogPublishPeerUiEvents.workspaceOpened,
            // This is a safe guard in case that the dialog is already open since the effect for displaying a dialog is lazy loaded, so this one will be executed first.
            dialogReuseUiEvents.dialogOpened,
            dialogReuseUiEvents.dialogClosed,
            dialogPublishUiEvents.dialogOpened,
            dialogPublishSuccessUiEvents.dialogOpened,
            dialogPublishPeerUiEvents.publishingInProgressDialogOpened,
            dialogPublishPeerUiEvents.publishingFailureDialogOpened,
            dialogShortcutsUiEvents.dialogOpened,
            dialogShortcutsUiEvents.dialogClosed,
            dialogFrameDeletedUiEvents.dialogOpened,
            dialogFrameDeletedUiEvents.dialogClosed,
          )
          .pipe(tap(() => store.cleanUpDialog())),
      };
    }),

    withHooks((store) => {
      return {
        onDestroy() {
          store.cleanUpDialog();
        },
      };
    }),
  );
}
